import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { usersApi } from "../../api/users";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function MetricCard({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 850 }}>{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>{value}</Typography>
          </Box>
        </Stack>
        {helper && <Typography sx={{ mt: 2, color: "rgba(25,36,47,0.62)" }}>{helper}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function SuperPanelPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [approvingId, setApprovingId] = React.useState("");

  const loadOverview = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await adminApi.overview();
      setOverview(data.overview);
    } catch (err) {
      setError(err.message || "No se pudo cargar el superpanel");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const approveAsClient = async (user) => {
    try {
      setApprovingId(user.id);
      setError("");
      await usersApi.approve(user.id, { globalRole: "client" });
      await loadOverview();
    } catch (err) {
      setError(err.message || "No se pudo aprobar el usuario");
    } finally {
      setApprovingId("");
    }
  };

  const totals = overview?.totals || {};

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Superpanel WebProfit
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            Tu vista interna para aprobar clientes, controlar proyectos y ordenar el onboarding.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button onClick={() => navigate("/dashboard/actividad")} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
            Actividad global
          </Button>
          <Button onClick={() => navigate("/dashboard/usuarios")} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
            Usuarios
          </Button>
          <Button onClick={() => navigate("/dashboard/proyectos")} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
            Proyectos
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && <Alert severity="info">Cargando superpanel...</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<PeopleAltRoundedIcon />} title="Usuarios" value={loading ? "..." : totals.users || 0} helper="Todos los usuarios registrados en WebProfit." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<PendingActionsRoundedIcon />} title="Pendientes" value={loading ? "..." : totals.pendingUsers || 0} helper="Cuentas esperando aprobación." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<StorefrontRoundedIcon />} title="Proyectos" value={loading ? "..." : totals.projects || 0} helper="Clientes/páginas creadas." />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard icon={<CheckCircleRoundedIcon />} title="Activos" value={loading ? "..." : totals.activeProjects || 0} helper="Proyectos operativos." />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Usuarios pendientes</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,0.62)" }}>Primero confirman email, después vos los aprobás como cliente.</Typography>
                </Box>
                <Button onClick={() => navigate("/dashboard/usuarios")} endIcon={<ArrowForwardRoundedIcon />} sx={{ color: BRAND.dark, textTransform: "none", fontWeight: 900 }}>
                  Ver todos
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {(overview?.pendingUsers || []).length === 0 && <Alert severity="success">No hay usuarios pendientes.</Alert>}
                {(overview?.pendingUsers || []).map((user) => (
                  <Box key={user.id} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,0.06)" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{user.name}</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>{user.email}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={user.emailVerified ? "Email ok" : "Sin verificar"} color={user.emailVerified ? "success" : "warning"} />
                        <Button disabled={approvingId === user.id} onClick={() => approveAsClient(user)} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                          Aprobar
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Últimos proyectos</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,0.62)" }}>Páginas/clientes cargados en el sistema.</Typography>
                </Box>
                <Button onClick={() => navigate("/dashboard/proyectos")} endIcon={<ArrowForwardRoundedIcon />} sx={{ color: BRAND.dark, textTransform: "none", fontWeight: 900 }}>
                  Ver todos
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {(overview?.latestProjects || []).length === 0 && <Alert severity="info">Todavía no hay proyectos.</Alert>}
                {(overview?.latestProjects || []).map((project) => (
                  <Box key={project.id} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,0.06)" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{project.name}</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>/{project.slug} · {project.plan} · {project.status}</Typography>
                      </Box>
                      <Button onClick={() => navigate(`/dashboard/proyectos/${project.slug}`)} sx={{ color: BRAND.orange, textTransform: "none", fontWeight: 900 }}>
                        Administrar
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
